import {
  createMutationStore,
  createQueryStore,
  useQueries,
  useQueryClient,
} from '@ferreri/minato-react-utils';
import { usePersistedGeolocation } from '@modules/geolocation/geolocation.store';

import { getCompanyEstablishment } from '../establishments/establishments.client';
import { Establishment } from '../establishments/establishments.types';
import { usePublicationIdQueryParam } from '../publications/publications.store';
import {
  getPublicationEstablishments,
  getPublicationEstablishmentsList,
  setPublicationEstablishments,
} from './publication-establishments.client';
import { GeolocationParams } from './publication-establishments.types';

export const usePublicationEstablishments = (
  publication,
  params?: GeolocationParams,
) => {
  const establishmentsIds = usePublicationEstablishmentsIds(params);
  const queries = usePublicationEstablishmentsQueries(
    publication?.company_id,
    establishmentsIds?.data,
  );

  const isLoading = queries.some((query) => query.isLoading);
  const isFetching = queries.some((query) => query.isFetching);
  const data = queries.map((query) => query.data) as Establishment[];

  return { data, isLoading, isFetching };
};

export const usePublicationEstablishmentsList = (params?: {
  latitude: number;
  longitude: number;
}) => {
  return createQueryStore({
    queryKey: [
      'publication_establishments_list',
      params?.latitude,
      params?.longitude,
    ],
    queryFn: () => getPublicationEstablishmentsList(params),
  });
};

const usePublicationEstablishmentsQueries = (companyId, establishmentsIds) => {
  return useQueries({
    queries: (establishmentsIds || []).map(
      ({ publication_id, establishment_id }) => {
        return {
          queryKey: [
            'publication_establishments',
            { publication_id, establishment_id },
          ],
          queryFn: () => getCompanyEstablishment(companyId, establishment_id),
          enabled: !!companyId && !!establishment_id,
        };
      },
    ),
  }).filter((query) => query.status === 'success');
};

export const usePublicationEstablishmentsIds = (params?: GeolocationParams) => {
  const publicationId = usePublicationIdQueryParam();

  return createQueryStore({
    queryKey: ['establishmentsIds', publicationId],
    queryFn: () => getPublicationEstablishments(publicationId, params),
    enabled: !!publicationId,
  });
};

export const useSetPublicationEstablishments = () => {
  const publicationId = usePublicationIdQueryParam();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: number[]) =>
      setPublicationEstablishments({ ...data, publication_id: publicationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['establishmentsIds', publicationId],
      });
    },
  });
};

export const usePublicationEstablishmentsListComposedGeolocation = () => {
  const { value } = usePersistedGeolocation();
  const { data, isLoading: isPublicationEstablishmentLoading } =
    usePublicationEstablishmentsList({
      latitude: value?.latitude,
      longitude: value?.longitude,
    });

  return { data, isLoading: isPublicationEstablishmentLoading };
};
